import React, { createContext, useContext, useEffect, useState } from "react";

const DISMISS_KEY = "rv_install_dismissed";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const InstallContext = createContext(null);

export const useInstall = () => useContext(InstallContext);

export const InstallProvider = ({ children }) => {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [standalone] = useState(() => isStandalone());
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    setIsIOS(/iphone|ipad|ipod/i.test(window.navigator.userAgent || ""));

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setDismissed(true);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  // Returns "accepted" | "dismissed" | "unavailable"
  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      if (outcome === "accepted") dismiss();
      return outcome;
    }
    return "unavailable";
  };

  const helpText = isIOS
    ? "Tap the Share icon in Safari, then choose \u201cAdd to Home Screen\u201d."
    : "Open your browser menu (\u22ee) and choose \u201cInstall app\u201d / \u201cAdd to Home Screen\u201d.";

  return (
    <InstallContext.Provider
      value={{ deferred, dismissed, dismiss, install, isIOS, helpText, canShow: !standalone }}
    >
      {children}
    </InstallContext.Provider>
  );
};
